import React from 'react';
import type { Editor } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import type { UnsplashImageSelection } from '../components/UnsplashImageLibraryDialog';

import {
  readTrimmedString,
  buildPlaceholderKey,
  isPositionInsideTableCell,
} from '@/shared/utils/documentImagePlaceholders';
import { getLiveEditor } from '@/shared/utils/tiptapEditor';

export interface PendingDocumentImageItem {
  key: string;
  id: string | null;
  imageQuery: string;
  pos: number;
}

function buildPendingItems(editor: Editor): PendingDocumentImageItem[] {
  const items: PendingDocumentImageItem[] = [];

  editor.state.doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (node.type.name !== 'imagePlaceholder') return;

    const id = readTrimmedString(node.attrs?.id) || null;
    items.push({
      key: buildPlaceholderKey(id, pos),
      id,
      imageQuery: readTrimmedString(node.attrs?.imageQuery),
      pos,
    });
  });

  return items;
}

function findPlaceholderByKey(
  editor: Editor,
  key: string
): { pos: number; node: ProseMirrorNode } | null {
  let result: { pos: number; node: ProseMirrorNode } | null = null;
  editor.state.doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (result) return false;
    if (node.type.name === 'imagePlaceholder') {
      const id = readTrimmedString(node.attrs?.id) || null;
      if (buildPlaceholderKey(id, pos) === key) {
        result = { pos, node };
        return false;
      }
    }
  });
  return result;
}

export function usePendingDocumentImages(editor: Editor | null) {
  const [items, setItems] = React.useState<PendingDocumentImageItem[]>([]);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = React.useCallback(() => {
    const instance = getLiveEditor(editor);
    if (!instance) {
      setItems([]);
      return [];
    }

    const nextItems = buildPendingItems(instance);
    setItems(nextItems);
    return nextItems;
  }, [editor]);

  React.useEffect(() => {
    const instance = getLiveEditor(editor);
    if (!instance) {
      setItems([]);
      return;
    }

    const onUpdate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(refresh, 300);
    };

    refresh();
    instance.on('update', onUpdate);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      instance.off('update', onUpdate);
    };
  }, [editor, refresh]);

  const focusPlaceholder = React.useCallback(
    (key: string) => {
      const instance = getLiveEditor(editor);
      if (!instance) return;
      const found = findPlaceholderByKey(instance, key);
      if (!found) return;

      const selection = NodeSelection.create(instance.state.doc, found.pos);
      const transaction = instance.state.tr.setSelection(selection).scrollIntoView();

      instance.view.dispatch(transaction);
      instance.view.focus();
    },
    [editor]
  );

  const resolvePlaceholder = React.useCallback(
    (key: string, selection: UnsplashImageSelection) => {
      const instance = getLiveEditor(editor);
      if (!instance) return null;
      const found = findPlaceholderByKey(instance, key);
      if (!found) return null;

      const matchingItem = items.find((item) => item.key === key);
      const nextId = matchingItem?.id || crypto.randomUUID();
      const attrs: Record<string, unknown> = {
        id: nextId,
        src: selection.src,
        alt: selection.alt,
        title: selection.title,
      };

      if (selection.storageBucket) attrs.storageBucket = selection.storageBucket;
      if (selection.storagePath) attrs.storagePath = selection.storagePath;

      if (isPositionInsideTableCell(instance.state.doc, found.pos)) {
        attrs.width = '100%';
      }

      instance
        .chain()
        .focus()
        .insertContentAt(
          { from: found.pos, to: found.pos + found.node.nodeSize },
          {
            type: 'image',
            attrs,
          }
        )
        .run();

      refresh();

      return key;
    },
    [editor, items, refresh]
  );

  return {
    items,
    count: items.length,
    refresh,
    focusPlaceholder,
    resolvePlaceholder,
  };
}
